import React from 'react';
import PropTypes from 'prop-types';
import { Trans } from 'react-i18next';
import i18n from '~shared/utils/i18n';

import {
    IPW_BASE_INPLAY_LINK,
    IPW_TITLE_ICON_CLASS_NAME,
    IPW_TITLE_LINK,
    IPW_FEATURED_IN_PLAY_TEXT,
    IPW_HOMEPAGE_NAME,
    IPW_INPLAY_NAME,
    IPW_GTM_SEE_ALL,
    IPW_BET_CARD_GTM_TAG,
    IPW_INPLAY,
} from './constants';
import {
    HOME_COLUMN,
    DRAW_COLUMN,
    AWAY_COLUMN,
    TWO_WAY_COLUMN_LENGTH,
    THREE_WAY_COLUMN_LENGTH,
    FIRST_SELECTION_INDEX,
    SECOND_SELECTION_INDEX,
    THIRD_SELECTION_INDEX,
} from '../../../shared/components/selection-columns/constants';

import WidgetContainer from '../../../shared/ui/WidgetContainer';
import BetCard from '../../../shared/components/react/BetCard/index';
import { selectionArrayToObject } from '~shared/utils/selections-utils';
import EventDescriptionWrapper from '~shared/components/react/EventDescription/EventDescriptionWrapper';

import classNames from 'classnames';
import '~shared/components/react/SportsWidget/styles/sports-widget.scss';
import '~scenes/inplay/components/styles/column-labels.scss';
import '../styles/InplayWidget.scss';

const ZERO_CONSTANT = 0;
export default class InplayWidget extends React.Component {

    static propTypes = {
        events: PropTypes.array,
        selections: PropTypes.object,
        isShowFooter: PropTypes.bool,
        markets_headers: PropTypes.object,
        baseInplayLink: PropTypes.string,
        mainClassName: PropTypes.string,
        titleIconClassName: PropTypes.string,
        source: PropTypes.string,
        titleLink: PropTypes.string,
    }

    static defaultProps = {
        events: null,
        selections: null,
        isShowFooter: false,
        markets_headers: {},
        baseInplayLink: IPW_BASE_INPLAY_LINK,
        mainClassName: 'bb-inplay-widget',
        titleIconClassName: IPW_TITLE_ICON_CLASS_NAME,
        titleLink: IPW_TITLE_LINK,
    }

    // @TODO to remove and don't set initial state
    state = {}

    // @TODO to remove and move refresh logic to container
    static getDerivedStateFromProps(props) {
        return props;
    }

    /**
     * Reorder selections if number of selections is less than
     * number of market headers
     * @param {Array} selections array of selections
     * @param {Number} columnsLength number of market columns
     * @returns {Array} ordered array of selections
     * @memberof InplayWidget
     */
    reorderSelections(selections, columnsLength) {
        const numberOfSelections = selections.length;

        if (!numberOfSelections || numberOfSelections === columnsLength) {
            return selections;
        }

        const mappedSelections = columnsLength === TWO_WAY_COLUMN_LENGTH
            ? [null, null]
            : [null, null, null];

        if (numberOfSelections < columnsLength && columnsLength === THREE_WAY_COLUMN_LENGTH) {
            selections.forEach(selection => {
                const selectionType = selection.selection_type;
                if (selectionType === HOME_COLUMN) {
                    mappedSelections[FIRST_SELECTION_INDEX] = selection;
                } else if (selectionType === DRAW_COLUMN) {
                    mappedSelections[SECOND_SELECTION_INDEX] = selection;
                } else if (selectionType === AWAY_COLUMN) {
                    mappedSelections[THIRD_SELECTION_INDEX] = selection;
                }
            });
        }

        if (numberOfSelections < columnsLength && columnsLength === TWO_WAY_COLUMN_LENGTH) {
            const [selection] = selections;
            const selectionType = selection.selection_type;

            if (selectionType === HOME_COLUMN) {
                mappedSelections[FIRST_SELECTION_INDEX] = selection;
            } else {
                mappedSelections[SECOND_SELECTION_INDEX] = selection;
            }
        }

        return mappedSelections;
    }

    getAndUpdateSelections(selections, marketHeaders, columnsLength = ZERO_CONSTANT) {

        const calculatedColumnsLength = parseInt(columnsLength, 10) > ZERO_CONSTANT ? parseInt(columnsLength, 10) : Object.entries(marketHeaders).length;
        const sortedSelections = this.reorderSelections(selections, calculatedColumnsLength);

        return sortedSelections.map((item) => {
            if (!item) {
                return {
                    betable: false,
                };
            }

            const itemSuspended = item.suspended || (!item.active || !item.tradable);
            const itemIsBetable = itemSuspended ? !itemSuspended : item.betable;

            return {
                ...item,
                betable: itemIsBetable,
                suspended: itemSuspended,
            };
        });
    }

    getSelectionsWithMarket(event, selections, marketsHeaders) {
        const marketHeaders = marketsHeaders[event.market_id] || event.default_market_headers;

        return this.getAndUpdateSelections(selections, marketHeaders, event.sport_columns);
    }

    renderEvents(events, selections, markets, marketsHeaders) {
        const { source: currentPage } = this.props;
        const widgetName = (currentPage === IPW_INPLAY) ? IPW_INPLAY_NAME : IPW_HOMEPAGE_NAME;
        const gtm = `${currentPage};${widgetName};`;
        const sportNames = new Set();

        const betCards = events
        .flatMap((event) => {
            const {
                id: eventId,
                sport_slug: sportSlug,
                sport_name: sportName,
                ordered_market_headers: marketHeaders = [],
            } = event;
            const eventSelections = selections[eventId] || [];
            const eventMarkets = markets[eventId] || {};
            const isBetable = !event.suspended && [].concat(...Object.values(eventSelections)).some(({suspended = true}) => !suspended);
            const { usesThreeMarketView, isUsFormat, grouped_market_ids: marketIds } = event;

            const selectionsBtns = usesThreeMarketView ? [] : this.getSelectionsWithMarket(event, eventSelections, marketsHeaders);
            const description = <EventDescriptionWrapper
                event={{
                    ...event,
                    inplay: true,
                    event_url: `${this.props.baseInplayLink}/${eventId}`,
                }}
                gtm={gtm + IPW_BET_CARD_GTM_TAG}
            />;

            let props = {
                description,
                betable: isBetable,
                selections: selectionsBtns,
                gtmAttribute: gtm,
                event,
            };
            if (usesThreeMarketView) {
                const markets = {};
                // changing market format to match the preplay endpoint.
                Object.keys(eventSelections).forEach(marketId => {
                    const selections = eventSelections[marketId] || [];

                    markets[marketId] = {
                        ...eventMarkets[marketId],
                        selections: selectionArrayToObject(selections),
                    };
                });
                props = {
                    ...props,
                    markets,
                    marketOrder: marketIds,
                    usesThreeMarketView: true,
                    isUsFormat,
                };
            }
            const shouldShowHeader = !sportNames.has(sportSlug);
            const shouldShowSportName = shouldShowHeader && currentPage !== sportSlug;
            sportNames.add(sportSlug);
            const headerClassnames = () => classNames(
                'bb-inplay-widget__sport-header',
                'bb-content-section__title',
                'bb-content-section__title--tertiary',
                {
                    'bb-inplay-widget__sport-header--three-markets': usesThreeMarketView,
                    'bb-inplay-widget__sport-header--no-sport-name': !shouldShowSportName,
                });
            const header = shouldShowHeader
                ? <div key={sportName} className={headerClassnames()}>
                    {shouldShowSportName && sportName}
                    <div className='bb-inplay-sport__column-labels bb-inplay-sport__column-labels__three-market-view'>
                        {
                            marketHeaders.map((headerName, index) => (
                                <div key={index} className='bb-inplay-sport__column-label'>
                                    {i18n.t(`mkt_headers.${headerName.toLowerCase()}`)}
                                </div>
                            ))
                        }
                    </div>
                </div>
                : null;

            return [header, <BetCard key={ eventId } { ...props } />];
        });

        return betCards;
    }

    render() {
        const { events, selections, markets, markets_headers } = this.state;

        if (!events || !selections) {
            return null;
        }

        const { isShowFooter, titleIconClassName } = this.props;
        const gtm = `${this.props.source};${IPW_HOMEPAGE_NAME};${IPW_GTM_SEE_ALL}`;

        const props = {
            title: <Trans>{IPW_FEATURED_IN_PLAY_TEXT}</Trans>,
            titleIconClassName,
            gtmAttribute: gtm,
        };

        if (isShowFooter) {
            props.titleLink = this.props.titleLink;
        }

        return (
            <div className='featured-matches-widget bb-inplay-widget'>
                <WidgetContainer { ...props } >
                    { this.renderEvents(events, selections, markets, markets_headers) }
                </WidgetContainer>
            </div>
        );
    }
}
